/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
// Extends from
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember imports
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { setDiff } from '@ember/object/computed';
// External imports
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
// Internal imports
import {
  destroyAndSaveRelated,
  loadRelated,
} from 'linced-admin/utils/destroy-record';

export default class IndexController extends Controller {
  // `jurisdictions` is what the API and the model hook call it; the URL shows
  // it as `jurisdictionsFilter`.
  queryParams = [{ jurisdictions: 'jurisdictionsFilter' }];

  Jurisdiction = Jurisdiction;

  // Services
  @service
  store;

  @service
  toaster;

  // Attributes
  @tracked
  currentSet = null;

  // Creation and updating attributes
  @tracked
  name = '';

  @action
  setName(value) {
    this.name = value;
  }

  @tracked
  description = '';

  @action
  setDescription(value) {
    this.description = value;
  }

  @tracked
  selectedJurisdictions = [];

  // Filters
  // jurisdictions is the query param, and the only stored filter state: the
  // numeric jurisdiction values, exactly as the API expects them.
  @tracked
  jurisdictions = [];

  // selectedJurisdictionsFilter maps those values to the enum objects the chips
  // component renders. Deriving it means a bookmarked URL populates the chips
  // for free.
  @computed('jurisdictions.[]')
  get selectedJurisdictionsFilter() {
    return this.jurisdictions
      .map((value) => Jurisdiction.getObject(value))
      .filter((jurisdiction) => !isEmpty(jurisdiction));
  }

  @setDiff('Jurisdiction.values', 'selectedJurisdictionsFilter')
  remainingJurisdictionsFilter;

  // Additional dialogs controls
  @tracked
  usageSetDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  deleteUsageSetDialogOpen = false;

  @tracked
  deleteInProgress = false;

  // Computed
  // jurisdictionsToAdd shadows the selectedJurisdictions to only store the numeric values
  // of the enum, so we can send the array to the db
  @computed('selectedJurisdictions.[]')
  get jurisdictionsToAdd() {
    if (isEmpty(this.selectedJurisdictions)) {
      return [];
    }
    return this.selectedJurisdictions.map((jurisdiction) => jurisdiction.value);
  }

  // remainingJurisdictions is used to display available jurisdictions in the chips
  // input upon editing/creating a usage set
  @setDiff('Jurisdiction.values', 'selectedJurisdictions')
  remainingJurisdictions;

  // Functions
  resetUsageSet() {
    this.currentSet = null;
    this.name = '';
    this.description = '';
    this.selectedJurisdictions = [];
  }

  // resetForm is used to reset dialogs to the initial state
  resetForm() {
    this.usageSetDialogOpen = false;
    this.saveInProgress = false;
    this.deleteUsageSetDialogOpen = false;
    this.deleteInProgress = false;
    // Filters
    this.jurisdictions = [];
  }

  // openDialog opens the creation/editing of a usage set dialog.
  @action
  openDialog() {
    this.usageSetDialogOpen = true;
  }

  // closeDialog closes the creation/editing of a usage set dialog.
  @action
  closeDialog() {
    this.usageSetDialogOpen = false;
    this.resetUsageSet();
  }

  // editSet pre-populates the necessary usage set attributes
  // and opens up the edit dialog
  @action
  editSet(id) {
    this.store
      .findRecord('schools/usage-set', id)
      .then((set) => {
        this.name = set.name;
        this.description = set.description;
        this.selectedJurisdictions = set.displayedJurisdictions;
        this.currentSet = set;
        this.openDialog();
      })
      .catch(() => {
        this.toaster.error(`Error editing the usage set`);
      });
  }

  @action
  addJurisdiction(jurisdiction) {
    this.selectedJurisdictions = [...this.selectedJurisdictions, jurisdiction];
  }

  @action
  removeJurisdiction(jurisdiction) {
    this.selectedJurisdictions = this.selectedJurisdictions.filter(
      (existing) => existing !== jurisdiction,
    );
  }

  @action
  addJurisdictionFilter(jurisdiction) {
    this.jurisdictions = [...this.jurisdictions, jurisdiction.value];
  }

  @action
  removeJurisdictionFilter(jurisdiction) {
    this.jurisdictions = this.jurisdictions.filter(
      (value) => value !== jurisdiction.value,
    );
  }

  // saveUsageSet saves a usage set and related subcomponents
  // when creating or editing usage sets
  @action
  saveUsageSet() {
    this.saveInProgress = true;
    let usageSet = this.currentSet;
    if (isEmpty(usageSet)) {
      // Creating a new usage set
      usageSet = this.store.createRecord('schools/usage-set', {
        name: this.get('name'),
        description: this.get('description'),
        jurisdictions: this.get('jurisdictionsToAdd'),
      });
    } else {
      // Updating the usage set
      usageSet.setProperties({
        name: this.get('name'),
        description: this.get('description'),
        jurisdictions: this.get('jurisdictionsToAdd'),
      });
    }
    usageSet
      .save()
      .then(() => {
        this.toaster.success(`Successfully saved the usage set`);
        this.resetForm();
        this.resetUsageSet();
      })
      .catch(() => {
        this.toaster.error(`Error saving the usage set`);
        usageSet.rollbackAttributes();
      })
      .then(() => {
        this.saveInProgress = false;
      });
  }

  // deleteSet pre-populates attributes and opens up the deletion dialog
  @action
  deleteSet(id) {
    this.store
      .findRecord('schools/usage-set', id)
      .then((set) => {
        this.currentSet = set;
        this.deleteUsageSetDialogOpen = true;
      })
      .catch(() => {
        this.toaster.error(`Error trying to delete the usage set`);
      });
  }

  @action
  async confirmDelete(confirmed) {
    if (!confirmed) {
      this.currentSet = null;
      this.deleteUsageSetDialogOpen = false;
      this.deleteInProgress = false;
      return;
    }

    this.deleteInProgress = true;
    const set = this.currentSet;

    // The records using the set get saved after the delete so the server
    // drops the set from them. Gather them first: after the delete the set's
    // relationships read as null (see destroyAndSaveRelated).
    const related = await loadRelated(set, [
      'pageIds',
      'goalSubjectIds',
      'milestoneSetIds',
      'groupTemplateIds',
      'assessmentTemplateIds',
      'tagCategoryTemplatesIds',
    ]);

    const { deleted, error } = await destroyAndSaveRelated(set, related);
    this.deleteInProgress = false;
    if (!deleted) {
      this.toaster.error(`Error trying to delete the usage set`);
      return;
    }
    if (error) {
      this.toaster.error(
        `The usage set was deleted, but removing it from the records that used it failed`,
      );
    }

    this.currentSet = null;
    this.deleteUsageSetDialogOpen = false;
  }
}
